import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, Package, Plus, Minus, Loader2, FileText, X, Car, Shield, AlertTriangle,
  Camera, Upload, Save
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { API } from "../../App";
import { UsagePurposePicker, noteRequiredFor, purposeMeta } from "./UsagePurpose";

// LocalStorage key generator for autosave
const getStorageKey = (bookingId) => `saturn_completion_${bookingId}`;

const WARRANTY_OPTIONS = [
  "Lifetime",
  "10 Years",
  "7 Years", 
  "5 Years",
  "3 Years",
  "1 Year",
  "None"
];

// Helper to format quantity display
const formatQuantity = (amount, baseUnit) => {
  if (baseUnit === "cm") {
    const meters = Math.floor(amount / 100);
    const cm = Math.round(amount % 100);
    if (meters > 0 && cm > 0) return `${meters}m ${cm}cm`;
    if (meters > 0) return `${meters}m`;
    return `${cm}cm`;
  }
  if (baseUnit === "ml") return `${Math.round(amount)}ml`;
  return `${Math.round(amount)} pcs`;
};

const JobCompletionModal = ({ isOpen, onClose, booking, onComplete }) => {
  const [floatUnits, setFloatUnits] = useState([]); // User's float stock units
  const [unitsByCategory, setUnitsByCategory] = useState({});
  const [categories, setCategories] = useState([]);
  const [materialsUsed, setMaterialsUsed] = useState([]); // Array of {unit_id, product_name, base_unit, amount_used, remaining_quantity}
  const [existingMaterials, setExistingMaterials] = useState([]); // Materials already logged during the job
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // After photos state
  const [afterPhotos, setAfterPhotos] = useState([]); // [{file, preview, uploading, photo_id}]
  const [existingAfterPhotos, setExistingAfterPhotos] = useState(0);
  const [jobWasCheckedIn, setJobWasCheckedIn] = useState(false);
  const afterPhotoInput = useRef(null);
  
  // Warranty and vehicle reg
  const [warrantyDuration, setWarrantyDuration] = useState("");
  // A redo visit is covered by the ORIGINAL job's warranty and never gets
  // one of its own, so Complete Job must not ask for one (the backend
  // refuses to write one either).
  const isRedo = !!booking?.is_redo;
  const [vehicleReg, setVehicleReg] = useState("");
  // Complimentary car wash — pre-ticked when the booking is entitled.
  // Staff can untick it ("wash not used"); the cost line is only written
  // when it is ticked. Staff never see what the wash costs.
  const [carWashDone, setCarWashDone] = useState(false);
  
  // Category and unit selection
  const washEntitled = !!booking?.wash_perk?.entitled;
  useEffect(() => {
    if (isOpen) setCarWashDone(washEntitled);
  }, [isOpen, washEntitled]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  
  // Autosave tracking
  const [hasRestoredData, setHasRestoredData] = useState(false);

  // Filter units by selected category
  const filteredUnits = selectedCategory 
    ? (unitsByCategory[selectedCategory] || [])
    : [];

  // Autosave to localStorage (debounced)
  const saveToStorage = useCallback(() => {
    if (!booking?.booking_id) return;
    
    const storageKey = getStorageKey(booking.booking_id);
    const dataToSave = {
      notes,
      // Only save uploaded photos (with photo_id)
      afterPhotos: afterPhotos
        .filter(p => p.photo_id)
        .map(p => ({ photo_id: p.photo_id, preview: p.preview })),
      // Save materials used with their amounts
      materialsUsed: materialsUsed.map(m => ({
        unit_id: m.unit_id,
        product_id: m.product_id,
        product_name: m.product_name,
        base_unit: m.base_unit,
        remaining_quantity: m.remaining_quantity,
        amount_used: m.amount_used,
        usage_purpose: m.usage_purpose,
        usage_note: m.usage_note
      })),
      warrantyDuration,
      vehicleReg,
      selectedCategory,
      savedAt: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (err) {
      console.warn("Failed to autosave completion data:", err);
    }
  }, [booking?.booking_id, notes, afterPhotos, materialsUsed, warrantyDuration, vehicleReg, selectedCategory]);

  // Autosave effect - runs when relevant state changes
  useEffect(() => {
    if (!isOpen || !booking?.booking_id || !hasRestoredData || isLoading) return;
    
    const timeoutId = setTimeout(() => {
      saveToStorage();
    }, 500); // Debounce 500ms
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, hasRestoredData, isLoading, saveToStorage]);

  // Clear storage on successful submission
  const clearStorage = useCallback(() => {
    if (!booking?.booking_id) return;
    const storageKey = getStorageKey(booking.booking_id);
    try {
      localStorage.removeItem(storageKey);
    } catch (err) {
      console.warn("Failed to clear autosave data:", err);
    }
  }, [booking?.booking_id]);

  // Load data and restore from localStorage
  useEffect(() => {
    if (isOpen && booking) {
      loadData();
    } else if (!isOpen) {
      setHasRestoredData(false);
    }
  }, [isOpen, booking]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load float units
      const floatRes = await axios.get(`${API}/stock/items/for-job`);
      setUnitsByCategory(floatRes.data.items_by_category || {});
      setCategories(floatRes.data.categories || []);
      
      // Flatten for easy lookup
      const allUnits = [];
      Object.values(floatRes.data.items_by_category || {}).forEach(units => {
        allUnits.push(...units);
      });
      setFloatUnits(allUnits);
      
      // Load materials already logged during the job (in-progress additions)
      try {
        const logRes = await axios.get(`${API}/stock/job-log/${booking.booking_id}`);
        setExistingMaterials(logRes.data?.materials_used || []);
      } catch {
        setExistingMaterials([]);
      }
      
      // Check if job was checked in and get existing after photos count
      if (booking?.booking_id) {
        try {
          const stageRes = await axios.get(`${API}/jobs/stage/${booking.booking_id}`);
          setJobWasCheckedIn(stageRes.data.checked_in || false);
          setExistingAfterPhotos(stageRes.data.after_photos_count || 0);
        } catch {
          setJobWasCheckedIn(false);
          setExistingAfterPhotos(0);
        }
      }
      
      // After loading data, try to restore from localStorage
      restoreFromStorage();
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load stock units");
      // Still try to restore saved data even if load fails
      restoreFromStorage();
    } finally {
      setIsLoading(false);
    }
  };

  const restoreFromStorage = () => {
    const storageKey = getStorageKey(booking?.booking_id);
    let restored = false;
    
    try {
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        
        // Restore saved data
        setNotes(parsed.notes || "");
        setAfterPhotos(parsed.afterPhotos || []);
        setMaterialsUsed(parsed.materialsUsed || []);
        setWarrantyDuration(parsed.warrantyDuration || "");
        
        if (parsed.vehicleReg) setVehicleReg(parsed.vehicleReg);
        else setVehicleReg(booking?.vehicle_reg || booking?.vehicle?.reg || "");
        
        if (parsed.selectedCategory) setSelectedCategory(parsed.selectedCategory);
        else setSelectedCategory("");
        
        setSelectedUnit("");
        
        // Notify user that data was restored
        if (parsed.afterPhotos?.length > 0 || parsed.materialsUsed?.length > 0 || parsed.notes || parsed.warrantyDuration) {
          toast.success("Previous completion data restored", {
            description: "Your unsaved work has been recovered",
            duration: 3000
          });
        }
        restored = true;
      }
    } catch (err) {
      console.warn("Failed to restore autosave data:", err);
    }
    
    // If no saved data, reset to defaults
    if (!restored) {
      setMaterialsUsed([]);
      setNotes("");
      setWarrantyDuration("");
      setAfterPhotos([]);
      setVehicleReg(booking?.vehicle_reg || booking?.vehicle?.reg || "");
      setSelectedCategory("");
      setSelectedUnit("");
    }
    
    setHasRestoredData(true);
  };

  // Handle after photo upload
  const handleAfterPhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.type && !file.type.startsWith("image/")) {
        // An MPO/HEIC from the phone gallery can arrive with an EMPTY or
        // non-standard MIME type, so a client-side type check silently
        // dropped real photos (Pav, Oct 2026). Only reject what is clearly
        // not an image, and SAY SO. The server validates the real bytes.
        toast.error(`${file.name} is not an image — use the Camera button`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      const photoEntry = { file, preview, uploading: true, photo_id: null };
      setAfterPhotos(prev => [...prev, photoEntry]);

      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await axios.post(
          `${API}/jobs/photos/upload?booking_id=${booking.booking_id}&photo_type=after`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        // Update photo entry with photo_id
        setAfterPhotos(prev => 
          prev.map(p => p.preview === preview 
            ? { ...p, uploading: false, photo_id: response.data.photo_id }
            : p
          )
        );

        // Add to job log's after_photos
        await axios.post(`${API}/jobs/add-after-photos/${booking.booking_id}`, [response.data.photo_id]);
        
        setExistingAfterPhotos(prev => prev + 1);
        toast.success("After photo uploaded");
      } catch (error) {
        console.error("Photo upload failed:", error);
        setAfterPhotos(prev => prev.filter(p => p.preview !== preview));
        toast.error(error.response?.data?.detail || "Failed to upload photo");
      }
    }
    
    if (afterPhotoInput.current) {
      afterPhotoInput.current.value = "";
    }
  };

  // Remove after photo
  const removeAfterPhoto = (preview) => {
    setAfterPhotos(prev => prev.filter(p => p.preview !== preview));
    URL.revokeObjectURL(preview);
  };

  // Add selected unit to materials
  const handleAddUnit = () => {
    if (!selectedUnit) {
      toast.error("Please select a unit first");
      return;
    }
    
    const unit = floatUnits.find(u => u.unit_id === selectedUnit);
    if (!unit) return;
    
    // Check if already added
    if (materialsUsed.some(m => m.unit_id === unit.unit_id)) {
      toast.info("This unit is already in your materials list");
      return;
    }
    
    setMaterialsUsed(prev => [...prev, {
      unit_id: unit.unit_id,
      product_id: unit.product_id,
      product_name: unit.name,
      base_unit: unit.base_unit,
      remaining_quantity: unit.remaining_quantity,
      amount_used: 0, // User will enter the amount
      usage_purpose: "customer_job",
      usage_note: ""
    }]);
    
    setSelectedUnit("");
    toast.success(`Added ${unit.name} to materials`);
  };

  // Update amount used for a material
  const handleAmountChange = (unitId, newAmount) => {
    if (newAmount < 0) return;
    
    const material = materialsUsed.find(m => m.unit_id === unitId);
    if (material && newAmount > material.remaining_quantity) {
      toast.error(`Cannot exceed remaining: ${formatQuantity(material.remaining_quantity, material.base_unit)}`);
      return;
    }
    
    setMaterialsUsed(prev => prev.map(m => 
      m.unit_id === unitId ? { ...m, amount_used: newAmount } : m
    ));
  };

  // Increment/decrement helpers based on unit type
  const getStepSize = (baseUnit) => {
    if (baseUnit === "cm") return 10; // 10cm increments for films
    if (baseUnit === "ml") return 10; // 10ml increments for liquids
    return 1; // 1 piece for others
  };

  // Why the material left the shelf. Staff set this; it never changes
  // how much stock moves, only who it is charged to.
  const handlePurposeChange = (unitId, purpose, note) => {
    setMaterialsUsed(prev => prev.map(m =>
      m.unit_id === unitId ? { ...m, usage_purpose: purpose, usage_note: note } : m
    ));
  };

  // Remove material from list
  const handleRemoveMaterial = (unitId) => {
    setMaterialsUsed(prev => prev.filter(m => m.unit_id !== unitId));
  };

  const handleSubmit = async () => {
    // Warranty must be selected before completion
    if (!warrantyDuration && !isRedo) {
      toast.error("Please select a warranty duration before completing");
      return;
    }

    // Validate after photos if job was checked in
    const totalAfterPhotos = existingAfterPhotos + afterPhotos.filter(p => p.photo_id).length;
    if (jobWasCheckedIn && totalAfterPhotos === 0) {
      toast.error("Please upload at least 1 after photo showing the completed work");
      return;
    }

    // Validate materials have amounts
    const materialsWithAmounts = materialsUsed.filter(m => m.amount_used > 0);
    if (materialsUsed.length > 0 && materialsWithAmounts.length === 0) {
      toast.error("Please enter amounts for the materials used");
      return;
    }

    // Goodwill / internal use both move money, so the note is required.
    // Blocked here as well as server-side so the whole completion isn't
    // rejected after the photos have already uploaded.
    const missingNote = materialsWithAmounts.find(
      m => noteRequiredFor(m.usage_purpose) && !(m.usage_note || "").trim()
    );
    if (missingNote) {
      toast.error(`Add a note for "${missingNote.product_name}" — say what the ${purposeMeta(missingNote.usage_purpose).short.toLowerCase()} was for`);
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert materials array to the format expected by API
      const materialsArray = materialsWithAmounts.map(m => ({
        unit_id: m.unit_id,
        amount_used: m.amount_used,
        usage_purpose: m.usage_purpose || "customer_job",
        usage_note: (m.usage_note || "").trim() || null
      }));

      const response = await axios.post(`${API}/job-logs/complete/${booking.booking_id}`, {
        shade_variant: null, // No longer tracking separately - materials show what was used
        materials_used: materialsArray,
        warranty_duration: warrantyDuration || null,
        vehicle_reg: vehicleReg.trim().toUpperCase() || null,
        notes: notes.trim() || null,
        car_wash_done: washEntitled ? carWashDone : false
      });

      // Clear autosave data on successful submission
      clearStorage();
      
      toast.success("Job completed successfully!");
      onComplete?.(response.data);
      onClose();
    } catch (error) {
      console.error("Failed to complete job:", error);
      const detail = error.response?.data?.detail;
      let errorMessage = "Failed to complete job";
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || d).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            Complete Job
          </DialogTitle>
          <DialogDescription className="text-slate-400 flex items-center justify-between">
            <span>Log materials used and complete the job for {booking.customer_name}</span>
            <span className="flex items-center gap-1 text-xs text-green-500">
              <Save className="w-3 h-3" />
              Auto-saving
            </span>
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-saturn-orange" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Job Summary */}
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />
                Job Summary
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-400">Customer:</span>{" "}
                  <span className="text-white">{booking.customer_name}</span>
                </div>
                <div>
                  <span className="text-slate-400">Vehicle:</span>{" "}
                  <span className="text-white">
                    {booking.vehicle?.year} {booking.vehicle?.make} {booking.vehicle?.model}
                  </span>
                </div>
              </div>
              <div className="text-sm">
                <span className="text-slate-400">Services:</span>{" "}
                <span className="text-saturn-orange">
                  {booking.services_details?.map(s => s.name).join(", ") || "N/A"}
                </span>
              </div>
            </div>

            {/* Vehicle Registration */}
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Car className="w-4 h-4 text-saturn-orange" />
                Vehicle Registration
              </Label>
              <Input
                value={vehicleReg}
                onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
                placeholder="e.g., 231-D-12345"
                className="bg-slate-800 border-slate-700 text-white uppercase"
                data-testid="vehicle-reg-input"
              />
              <p className="text-slate-500 text-xs">For warranty tracking purposes</p>
            </div>

            {/* Complimentary car wash — only when the booking is entitled */}
            {washEntitled && (
              <div className="space-y-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3"
                   data-testid="car-wash-done-block">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={carWashDone}
                    onChange={(e) => setCarWashDone(e.target.checked)}
                    className="mt-0.5 accent-cyan-400 w-4 h-4"
                    data-testid="car-wash-done-checkbox"
                  />
                  <span>
                    <span className="text-white text-sm font-medium">Car wash done</span>
                    <span className="block text-slate-400 text-xs">
                      This customer's booking includes a complimentary wash.
                      Untick it if the wash wasn't used.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* After Photos - Required if job was checked in */}
            {jobWasCheckedIn && (
              <div className="space-y-3">
                <Label className="text-white flex items-center gap-2">
                  <Camera className="w-4 h-4 text-green-500" />
                  <span className="text-red-400">*</span>
                  After Photos (Completed Work)
                </Label>
                <p className="text-slate-500 text-xs">
                  Take photos showing the completed work. At least 1 photo required.
                </p>
                
                {existingAfterPhotos > 0 && afterPhotos.length === 0 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-2 text-sm text-green-400">
                    <CheckCircle2 className="w-4 h-4 inline mr-1" />
                    {existingAfterPhotos} after photo(s) already uploaded
                  </div>
                )}
                
                <div className="grid grid-cols-4 gap-2">
                  {afterPhotos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-slate-800">
                      <img 
                        src={photo.preview} 
                        alt={`After ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {photo.uploading && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <Loader2 className="w-5 h-5 animate-spin text-green-500" />
                        </div>
                      )}
                      {photo.photo_id && (
                        <div className="absolute top-1 right-1">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        </div>
                      )}
                      <button
                        onClick={() => removeAfterPhoto(photo.preview)}
                        className="absolute bottom-1 right-1 p-1 bg-red-500/80 rounded hover:bg-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Camera Button */}
                  <label className="aspect-square rounded-lg border-2 border-dashed border-green-500/50 bg-green-500/10 flex flex-col items-center justify-center cursor-pointer hover:border-green-500 hover:bg-green-500/20 transition-colors">
                    <Camera className="w-6 h-6 text-green-500 mb-1" />
                    <span className="text-xs text-green-500">Camera</span>
                    <input
                      ref={afterPhotoInput}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleAfterPhotoSelect}
                      className="hidden"
                      data-testid="after-photo-camera"
                    />
                  </label>
                  
                  {/* Gallery Button */}
                  <label className="aspect-square rounded-lg border-2 border-dashed border-slate-600 flex flex-col items-center justify-center cursor-pointer hover:border-slate-500 transition-colors">
                    <Upload className="w-5 h-5 text-slate-500 mb-1" />
                    <span className="text-xs text-slate-500">Gallery</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAfterPhotoSelect}
                      className="hidden"
                      data-testid="after-photo-gallery"
                    />
                  </label>
                </div>
                
                {(existingAfterPhotos === 0 && afterPhotos.filter(p => p.photo_id).length === 0) && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-sm text-red-400">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    At least 1 after photo is required to complete the job
                  </div>
                )}
              </div>
            )}

            {/* Warranty Duration — never asked for on a redo visit: the
                original job's warranty still stands and a redo does not
                start a new one. */}
            {isRedo ? (
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3"
                   data-testid="redo-no-warranty-note">
                <p className="text-amber-200 text-sm font-medium">
                  Redo visit — no warranty needed
                </p>
                <p className="text-amber-200/70 text-xs mt-1">
                  The original job&apos;s warranty still applies. Nothing to
                  collect and no invoice on this visit.
                </p>
              </div>
            ) : (
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-500" />
                <span className="text-red-400">*</span>
                Warranty Duration
              </Label>
              <Select value={warrantyDuration} onValueChange={setWarrantyDuration}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="warranty-select">
                  <SelectValue placeholder="Select warranty period..." />
                </SelectTrigger>
                <SelectContent>
                  {WARRANTY_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            )}

            {/* Materials Used */}
            <div className="space-y-3">
              <Label className="text-white flex items-center gap-2">
                <Package className="w-4 h-4 text-saturn-orange" />
                Materials Used (from Shared Float)
              </Label>
              <p className="text-slate-500 text-xs">
                Select the materials used for this job. The tint film name shows the shade/VLT applied.
              </p>

              {existingMaterials.length > 0 && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 space-y-1" data-testid="existing-materials-list">
                  <span className="text-xs text-green-400 font-medium block mb-1">Already logged during the job:</span>
                  {existingMaterials.map((m, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-300 truncate">{m.product_name}</span>
                      <span className="text-slate-400 ml-2 whitespace-nowrap">{m.amount_display || formatQuantity(m.amount_used, m.base_unit)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {floatUnits.length === 0 ? (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <p className="text-yellow-200 text-sm">
                    No stock in shared float. Pull items from storage first.
                  </p>
                </div>
              ) : (
                <>
                  {/* Step 1: Select Category */}
                  <div className="space-y-2">
                    <Select value={selectedCategory} onValueChange={(val) => {
                      setSelectedCategory(val);
                      setSelectedUnit(""); // Reset unit when category changes
                    }}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="category-select">
                        <SelectValue placeholder="1. Select category..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Step 2: Select Unit from Category */}
                  {selectedCategory && (
                    <div className="flex gap-2">
                      <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                        <SelectTrigger className="flex-1 bg-slate-800 border-slate-700 text-white" data-testid="stock-unit-select">
                          <SelectValue placeholder="2. Select unit..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {filteredUnits.length > 0 ? (
                            filteredUnits.map(unit => (
                              <SelectItem 
                                key={unit.unit_id} 
                                value={unit.unit_id}
                                disabled={materialsUsed.some(m => m.unit_id === unit.unit_id)}
                              >
                                <span className="flex items-center gap-2">
                                  {unit.name} • {unit.remaining_display}
                                  <span className="text-xs text-slate-500">(by {unit.pulled_by || "Unknown"})</span>
                                </span>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="_empty" disabled>No units in this category</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={handleAddUnit}
                        disabled={!selectedUnit}
                        className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        data-testid="add-unit-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Materials List */}
              {materialsUsed.length > 0 ? (
                <div className="space-y-2 max-h-[250px] overflow-y-auto">
                  {materialsUsed.map(material => (
                    <div
                      key={material.unit_id}
                      className="bg-slate-800/50 rounded-lg p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-white truncate block">{material.product_name}</span>
                          <span className="text-xs text-slate-500">
                            Available: {formatQuantity(material.remaining_quantity, material.base_unit)}
                          </span>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          onClick={() => handleRemoveMaterial(material.unit_id)}
                          data-testid={`remove-${material.unit_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Amount Input */}
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-slate-400 w-16">
                          {material.base_unit === "cm" ? "cm used:" : 
                           material.base_unit === "ml" ? "ml used:" : "pcs used:"}
                        </Label>
                        <div className="flex items-center gap-1 flex-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-slate-400 hover:text-white"
                            onClick={() => handleAmountChange(
                              material.unit_id, 
                              Math.max(0, material.amount_used - getStepSize(material.base_unit))
                            )}
                            data-testid={`decrease-${material.unit_id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            type="number"
                            step={getStepSize(material.base_unit)}
                            min="0"
                            max={material.remaining_quantity}
                            value={material.amount_used}
                            onChange={(e) => handleAmountChange(material.unit_id, parseFloat(e.target.value) || 0)}
                            className="w-24 h-8 text-center bg-slate-900 border-slate-700 text-white text-sm"
                            data-testid={`amount-${material.unit_id}`}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-slate-400 hover:text-white"
                            onClick={() => handleAmountChange(
                              material.unit_id, 
                              Math.min(material.remaining_quantity, material.amount_used + getStepSize(material.base_unit))
                            )}
                            data-testid={`increase-${material.unit_id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        {material.amount_used > 0 && (
                          <span className="text-xs text-saturn-orange">
                            = {formatQuantity(material.amount_used, material.base_unit)}
                          </span>
                        )}
                      </div>

                      {/* What was it for? Never changes how much stock
                          moves — only who it is charged to. */}
                      <div className="pt-1 border-t border-slate-700/60">
                        <Label className="text-[11px] text-slate-400 block mb-1">
                          What was this used for?
                        </Label>
                        <UsagePurposePicker
                          purpose={material.usage_purpose || "customer_job"}
                          note={material.usage_note || ""}
                          onChange={(purpose, note) =>
                            handlePurposeChange(material.unit_id, purpose, note)}
                          idSuffix={`-${material.unit_id}`}
                          compact
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                floatUnits.length > 0 && (
                  <p className="text-slate-500 text-sm text-center py-4 bg-slate-800/30 rounded">
                    No materials added yet. Select items above to add.
                  </p>
                )
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />
                Notes (optional)
              </Label>
              <Textarea
                placeholder="Any issues, observations, or notes for this job..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white min-h-[80px]"
                data-testid="job-notes"
              />
              <p className="text-slate-500 text-xs">
                Please avoid pasting customer PII (plate numbers, addresses) — those belong in their dedicated fields.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (!warrantyDuration && !isRedo) || (jobWasCheckedIn && (existingAfterPhotos + afterPhotos.filter(p => p.photo_id).length) === 0)}
            className="bg-saturn-orange hover:bg-saturn-orange/80"
            data-testid="complete-job-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Complete Job
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default JobCompletionModal;
